import { Actions, createEffect, ofType } from '@ngrx/effects';
import { inject } from '@angular/core';
import { catchError, EMPTY, map, share, switchMap, withLatestFrom } from 'rxjs';
import { Store } from '@ngrx/store';
import { OrganizationModels } from '@share-models';
import { TranslateService } from '@ngx-translate/core';
import { OrganizationService } from '../../services/organization.service';
import { OrganizationActions } from '../state/organization/organization.actions';
import { SharedActions } from '../state/shared/shared.actions';
import { AuthActions } from '../state/auth/auth.action-types';
import { AppState } from '../app.state';
import { organizationFeature } from '../state/organization/organization.feature';
import { userFeature } from '../state/user/user.feature';

export const loadActiveOrganization = createEffect(
  (
    actions$ = inject(Actions),
    organizationService = inject(OrganizationService),
  ) => {
    return actions$.pipe(
      ofType(OrganizationActions.getActiveOrganization),
      switchMap(() =>
        organizationService.getActiveOrganization().pipe(
          map((activeOrganization) =>
            OrganizationActions.setActiveOrganizationDocument({
              activeOrganizationDocument: activeOrganization,
            }),
          ),
          catchError(() => EMPTY),
        ),
      ),
    );
  },
  { functional: true },
);

export const updateOrganizationSettings = createEffect(
  (
    actions$ = inject(Actions),
    organizationService = inject(OrganizationService),
    store$ = inject(Store<AppState>),
    translateService = inject(TranslateService),
  ) => {
    return actions$.pipe(
      ofType(OrganizationActions.updateSettings),
      withLatestFrom(
        store$.select(organizationFeature.selectActiveOrganizationDocument),
      ),
      switchMap(([{ organization }, activeOrganization]) => {
        if (!activeOrganization?._id) {
          return [OrganizationActions.updateSettingsError()];
        }

        const updatedOrganization: OrganizationModels.OrganizationDocument = {
          ...activeOrganization,
          settings: {
            ...activeOrganization.settings,
            orgName: organization.orgName,
            lang: organization.lang,
            startDayOfWeek: organization.startDayOfWeek,
          },
        };

        return organizationService
          .updateSettings({
            orgId: activeOrganization._id,
            orgName: organization.orgName,
            lang: organization.lang,
            startDayOfWeek: organization.startDayOfWeek,
          })
          .pipe(
            switchMap(() => {
              const organizationEntity: string = translateService.instant(
                'TOASTS.ORGANIZATION',
              ) as string;
              const success: string = translateService.instant(
                'TOASTS.ENTITY_UPDATE_SUCCESS',
                { entity: organizationEntity },
              ) as string;
              return [
                OrganizationActions.updateSettingsSuccess({
                  activeOrganizationDocument: updatedOrganization,
                }),
                SharedActions.showMessage({ success }),
              ];
            }),
            catchError(() => {
              const organizationEntity: string = translateService.instant(
                'TOASTS.ORGANIZATION',
              ) as string;
              const error: string = translateService.instant(
                'TOASTS.ENTITY_UPDATE_FAILED',
                { entity: organizationEntity },
              ) as string;
              return [
                OrganizationActions.updateSettingsError(),
                SharedActions.showMessage({ error }),
              ];
            }),
          );
      }),
    );
  },
  { functional: true },
);

export const pressSave$ = createEffect(
  (actions$ = inject(Actions)) => {
    return actions$.pipe(ofType(OrganizationActions.pressSave), share());
  },
  { functional: true, dispatch: false },
);

export const leaveOrg = createEffect(
  (
    actions$ = inject(Actions),
    organizationService = inject(OrganizationService),
    store$ = inject(Store<AppState>),
    translateService = inject(TranslateService),
  ) => {
    return actions$.pipe(
      ofType(OrganizationActions.leaveOrg),
      withLatestFrom(store$.select(userFeature.selectProfile)),
      switchMap(([{ orgId }, userProfile]) => {
        if (!userProfile?._id) {
          return [OrganizationActions.resetToInitialState()];
        }
        return organizationService.leave(userProfile._id, orgId).pipe(
          switchMap(() => {
            const success: string = translateService.instant(
              'TOASTS.LEAVE_ORG_SUCCESS',
            ) as string;
            return [
              SharedActions.showMessage({ success }),
              AuthActions.logout({ params: {} }),
            ];
          }),
          catchError(() => {
            const organizationEntity: string = translateService.instant(
              'TOASTS.ORGANIZATION',
            ) as string;
            const error: string = translateService.instant(
              'TOASTS.ENTITY_UPDATE_FAILED',
              { entity: organizationEntity },
            ) as string;
            return [SharedActions.showMessage({ error })];
          }),
        );
      }),
    );
  },
  { functional: true },
);

export const deleteOrg = createEffect(
  (
    actions$ = inject(Actions),
    organizationService = inject(OrganizationService),
    translateService = inject(TranslateService),
  ) => {
    return actions$.pipe(
      ofType(OrganizationActions.deleteOrg),
      switchMap(({ orgId }) =>
        organizationService.delete(orgId).pipe(
          switchMap(() => {
            const success: string = translateService.instant(
              'TOASTS.DELETE_ORG_QUEUED',
            ) as string;
            return [
              SharedActions.showMessage({ success }),
              AuthActions.logout({ params: {} }),
            ];
          }),
          catchError(() => {
            const organizationEntity: string = translateService.instant(
              'TOASTS.ORGANIZATION',
            ) as string;
            const error: string = translateService.instant(
              'TOASTS.ENTITY_UPDATE_FAILED',
              { entity: organizationEntity },
            ) as string;
            return [SharedActions.showMessage({ error })];
          }),
        ),
      ),
    );
  },
  { functional: true },
);
